import {
    Component,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    debounceTime, filter, firstValueFrom, map,
    startWith,
    timer,
} from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ApolloService, getCollections_collections, Order_By } from 'src/gql-generated/generated';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { SentryService } from 'src/app/services/sentry.service';
import { ConnectionService } from 'src/app/services/connection.service';
import { DynamicDialogComponent } from '../shared/dynamic-dialog/dynamic-dialog.component';
import { CollectionEditComponent } from './collection-edit/collection-edit.component';
import { CollectionDetailComponent } from './collection-detail/collection-detail.component';
import { NgIf, NgFor, NgClass, SlicePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { UserService } from 'src/app/services/user.service';

@Component({
    selector: 'app-collections',
    templateUrl: './collections.component.html',
    styleUrl: './collections.component.scss',
    imports: [
        NgIf,
        MatButton,
        RouterLink,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        NgFor,
        NgClass,
        MatIcon,
        MatTooltip,
        CollectionEditComponent,
        CollectionDetailComponent,
        DynamicDialogComponent,
        SlicePipe,
    ],
})
export class CollectionsComponent {
    /**
     * Reference to dialog that prevents user from leaving page on accident
     */
    @ViewChild('cancelDialog') cancelDialog?: DynamicDialogComponent;

    /**
     * Reference to collection edit component. Only exists when mode is 'edit' or 'new'.
     */
    @ViewChild(CollectionEditComponent) collectionEditComponent?: CollectionEditComponent;

    /**
     * Reference to collection detail component. Only exists when mode is 'detail'.
     */
    @ViewChild(CollectionDetailComponent) collectionDetailComponent?: CollectionDetailComponent;

    /**
     * All collections currently available (non-deleted)
     */
    collections?: getCollections_collections[];

    /**
     * Tracks the currently selected collection
     */
    selectedCollectionControl = new FormControl<getCollections_collections | undefined>(undefined);

    /**
     * Control tracking the input that is used to filter collections
     */
    searchCollectionsControl = new FormControl<string>('', { nonNullable: true });

    /**
     * Filtered collections based on searchCollectionsControl
     */
    filteredCollections?: getCollections_collections[];

    /**
     * Tracks which mode the user is currently operating in.
     */
    mode?: 'detail' | 'edit' | 'new';

    constructor(
        private apollo: ApolloService,
        private snackbar: SnackbarService,
        private route: ActivatedRoute,
        private router: Router,
        public breakpoint: BreakpointService,
        private organization: OrganizationService,
        private sentry: SentryService,
        public connection: ConnectionService,
        private user: UserService,
    ) {
        // Subscribe to route changes in order to track mode changes
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
            takeUntilDestroyed(),
        ).subscribe(() => {
            // Collections might have been updated (e.g. after an edit/new)
            this.getCollections().then(() => this.determineMode());
        });

        // Subscribe to search changes in order to update filteredCollections
        this.searchCollectionsControl.valueChanges.pipe(
            debounceTime(300),
            takeUntilDestroyed(),
            map((search) => search.toLocaleLowerCase().trim()),
        ).subscribe((search) => {
            this.filteredCollections = this.collections?.filter((collection) => collection.name.toLocaleLowerCase().trim().includes(search));
        });

        /**
         * Refresh collections every 10 seconds
         */
        timer(0, 10000).pipe(takeUntilDestroyed()).subscribe(() => {
            this.getCollections();
            // Force update of detail component
            this.collectionDetailComponent?.getCollection();
        });
    }

    /**
     * Handle user pressing the new collection button in the top left of the collections list
     */
    addCollection(): void {
        this.selectedCollectionControl.reset();
        this.mode = 'new';
    }

    /**
     * Gather route information and determine which mode the user is currently in
     */
    determineMode(): void {
        const collectionId = this.route.snapshot.paramMap.get('collectionId') || undefined;
        const urlEnd = window.location.href.split('/').at(-1);
        if (collectionId) {
            // Find the corresponding collection and update selectedCollectionControl
            if (this.collections) {
                if (this.selectedCollectionControl.value?.id !== collectionId) {
                    const collection = this.collections.find((col) => col.id === collectionId);
                    // If collection was not found, redirect to collection page
                    if (!collection) {
                        this.router.navigate(['collections'], { replaceUrl: true });
                    } else {
                        // Else, update the control
                        this.selectedCollectionControl.setValue(collection);
                    }
                }
                this.mode = urlEnd === 'edit' ? 'edit' : 'detail';
            }
            // If url ends in 'new' the user must be editing
        } else if (urlEnd === 'new') {
            this.mode = 'new';
            // If none if the above holds (e.g. route is `/collections`) route to detail of the first available collection on non-mobile
        } else if (this.collections?.length) {
            if (this.breakpoint.md.value) {
                this.router.navigate(['collections', this.collections[0]?.id], { replaceUrl: true });
            } else {
                this.selectedCollectionControl.reset();
            }
            // Finally, if there are no collections yet, set the mode to 'new' to encourage the user to create a first collection
        } else {
            this.router.navigate(['collections', 'new'], { replaceUrl: true });
        }
    }

    /**
     * Get all collections currently available (non-deleted)
     */
    async getCollections(): Promise<void> {
        try {
            const collectionsResponse = await firstValueFrom(this.apollo.getCollections(
                {
                    where: {
                        deleted_at: { _is_null: true },
                        organization_id: { _eq: this.organization.currentOrganization$.value?.id },
                        _or: [
                            {
                                private: { _eq: true },
                                created_by_id: { _eq: this.user.profile.id },
                            },
                            {
                                private: { _eq: false },
                            },
                        ],
                    },
                    order_by: [
                        {
                            updated_at: Order_By.Desc,
                        },
                        {
                            created_at: Order_By.Desc,
                        },
                    ],
                    limit: 9999,
                }),
            );
            this.collections = collectionsResponse.data?.collections || [];
            this.filteredCollections = this.collections.filter((collection) => collection.name.toLocaleLowerCase()
                .trim().includes(this.searchCollectionsControl.value));
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Kan collecties niet laden`);
        }
    }

    /**
     * Handle clicking of a collection card
     * @param collection Collection
     */
    selectCollection(collection: getCollections_collections): void {
        this.selectedCollectionControl.setValue(collection);
        this.mode = 'detail';
    }

    /**
     * Checks if user should be warned before leaving the page
     * @returns Whether user is allowed to leave the page
     */
    canDeactivate(): boolean | Promise<boolean> {
        // Make sure dialog is available
        if (!this.cancelDialog) {
            return true;
        }
        // Prevent user from leaving with changes to collectionFormgroup of collectionEditComponent
        if (this.collectionEditComponent?.collectionFormGroup.touched) {
            return this.cancelDialog.open();
        }

        // Prevent user from leaving with changes to collectionFormgroup of collectionEditComponent
        if (this.collectionEditComponent?.collectionFormGroup.touched) {
            return this.cancelDialog.open();
        }
        // If selectedCollectionControl has no value the user is not on an edit/new page, save to leave
        if (!this.selectedCollectionControl.value) {
            return true;
        }
        // Allow leave by default
        return true;
    }

    /**
     * Functions used as trackByFunction in ngFor loop in template
     * @param index Index of collection
     * @param collection Collection
     * @returns Id of collection
     */
    collectionById(index: number, collection: getCollections_collections): string {
        return collection.id;
    }
}
